import moment, { Moment } from 'moment-timezone';


export enum MediaEditorTab {
    PROGRAM = 'ModProgramInfo',
    VIEWSHIP = 'ModViewership',
    EDITOR = 'ModEditor'
}

/**
 * unsigned, human-readable params for tvEyes iframe
 * use TVEyesMentionsService::getMediaViewUrlFromPlayerlette() or TVEyesMentionsService::getMediaViewUrl() to get a freshly-signed `mediaview` URL
*/
export class TVEyesPlayerletteUrl {
    static readonly START_DATE_TIME = 'StartDateTime';

    private static readonly END_DATE_TIME = 'EndDateTime';
    private static readonly DURATION = 'Duration';
    private static readonly STATION_ID = 'StationID';
    private static readonly EDITOR_START_DATE_TIME = 'ModEditorDefaultStartPoint';
    private static readonly EDITOR_END_DATE_TIME = 'ModEditorDefaultEndPoint';
    private static readonly DEFAULT_TAB = 'DefaultModule';


    private mentionUrl: string;
    private startDateTime: Moment;
    private duration: number;
    private stationId: number;

    private editorStartDateTime: Moment;
    private editorEndDateTime: Moment;
    private defaultTab: MediaEditorTab;


    constructor(
        mentionUrl: string,
        startDateTime: Moment,
        duration: number,
        stationId: number,
        editorStartDateTime?: Moment,
        editorEndDateTime?: Moment,
        defaultTab?: MediaEditorTab
    ) {
        this.startDateTime = startDateTime;
        this.duration = duration;
        this.stationId = stationId;
        this.editorStartDateTime = editorStartDateTime;
        this.editorEndDateTime = editorEndDateTime;
        this.defaultTab = defaultTab;

        const url = new URL(mentionUrl);
        url.searchParams.set(TVEyesPlayerletteUrl.START_DATE_TIME, this.startDateTime.format('YYYY-MM-DD HH:mm:ss.SSS'));
        url.searchParams.set(TVEyesPlayerletteUrl.DURATION, this.duration.toString());
        url.searchParams.set(TVEyesPlayerletteUrl.STATION_ID, this.stationId.toString());

        if (this.editorStartDateTime) {
            url.searchParams.set(TVEyesPlayerletteUrl.EDITOR_START_DATE_TIME, this.editorStartDateTime.format('YYYY-MM-DD HH:mm:ss.SSS'));
        }

        if (this.editorEndDateTime) {
            url.searchParams.set(TVEyesPlayerletteUrl.EDITOR_END_DATE_TIME, this.editorEndDateTime.format('YYYY-MM-DD HH:mm:ss.SSS'));
        }

        if (this.defaultTab) {
            url.searchParams.set(TVEyesPlayerletteUrl.DEFAULT_TAB, this.defaultTab);
        }

        this.mentionUrl = url.toString();
    }

    static fromUrl(mentionUrl: string): TVEyesPlayerletteUrl {
        const params = new URLSearchParams(mentionUrl);
        const startDateTimeParam = moment.utc(params.get(TVEyesPlayerletteUrl.START_DATE_TIME));
        let durationParam = Number(params.get(TVEyesPlayerletteUrl.DURATION));
        if (durationParam === 0) {
            const endDateTimeParam = moment.utc(params.get(TVEyesPlayerletteUrl.END_DATE_TIME));
            if (endDateTimeParam) {
                durationParam = endDateTimeParam.diff(startDateTimeParam, 'seconds');
            }
        }
        const stationIdParam = Number(params.get(TVEyesPlayerletteUrl.STATION_ID));

        let editorStartTime: Moment;
        if (params.get(TVEyesPlayerletteUrl.EDITOR_START_DATE_TIME)) {
            editorStartTime = moment.utc(params.get(TVEyesPlayerletteUrl.EDITOR_START_DATE_TIME));
        }

        let editorEndTime: Moment;
        if (params.get(TVEyesPlayerletteUrl.EDITOR_END_DATE_TIME)) {
            editorEndTime = moment.utc(params.get(TVEyesPlayerletteUrl.EDITOR_END_DATE_TIME));
        }

        let defaultTab: MediaEditorTab;
        if (params.get(TVEyesPlayerletteUrl.DEFAULT_TAB)) {
            defaultTab = params.get(TVEyesPlayerletteUrl.DEFAULT_TAB) as MediaEditorTab;
        }


        return new TVEyesPlayerletteUrl(
            mentionUrl,
            startDateTimeParam,
            durationParam,
            stationIdParam,
            editorStartTime,
            editorEndTime,
            defaultTab
        );
    }

    getMentionUrl(): string {
        return this.mentionUrl;
    }

    getStartDateTime(): Moment {
        return this.startDateTime;
    }

    setStartDateTime(startDateTime: Moment) {
        this.startDateTime = startDateTime;
    }

    getDuration(): number {
        return this.duration;
    }

    getEndDateTime(): Moment {
        return moment.utc(this.startDateTime).add(this.duration, 'second');
    }

    getMediaViewEndDateTime(): Moment {
        return moment.utc(this.startDateTime).add(10, 'minutes');
    }

    getStationId(): number {
        return this.stationId;
    }

    clone(): TVEyesPlayerletteUrl {
        return new TVEyesPlayerletteUrl(
            this.mentionUrl,
            this.startDateTime,
            this.duration,
            this.stationId
        );
    }
}
